import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import useThemeStyles from '../../../hooks/useThemeStyles';

const BOX_WIDTH = 180;
const HEADER_HEIGHT = 28;
const FIELD_HEIGHT = 18;
const H_GAP = 70;
const V_GAP = 50;
const COLUMNS = 3;

const CARDINALITY = {
  'one-one': ['1', '1'],
  'one-many': ['1', 'N'],
  'many-many': ['N', 'N'],
};

/**
 * Auto-rendered ERD. Entities are laid out on a grid; relations drawn as
 * lines with cardinality markers. Pure render — no interaction.
 */
const ErdDiagram = ({ entities, relations }) => {
  const { colors } = useThemeStyles();

  if (entities.length === 0) {
    return null;
  }

  const boxes = new Map();
  let maxRowHeight = 0;
  let y = 20;
  let maxX = 0;

  entities.forEach((entity, index) => {
    const column = index % COLUMNS;
    if (column === 0 && index > 0) {
      y += maxRowHeight + V_GAP;
      maxRowHeight = 0;
    }
    const height = HEADER_HEIGHT + Math.max(entity.fields.length, 1) * FIELD_HEIGHT + 8;
    const x = 20 + column * (BOX_WIDTH + H_GAP);
    boxes.set(entity.id, { x, y, height, entity });
    maxRowHeight = Math.max(maxRowHeight, height);
    maxX = Math.max(maxX, x + BOX_WIDTH);
  });

  const totalHeight = y + maxRowHeight + 30;
  const totalWidth = maxX + 20;

  const center = (box) => ({ x: box.x + BOX_WIDTH / 2, y: box.y + box.height / 2 });

  return (
    <Box sx={{ overflowX: 'auto', border: `1px solid ${colors.border.subtle}`, borderRadius: 2 }}>
      <svg
        width={totalWidth}
        height={totalHeight}
        viewBox={`0 0 ${totalWidth} ${totalHeight}`}
        role="img"
        aria-label="Entity relationship diagram"
      >
        {/* Relations */}
        {relations.map((relation) => {
          const fromBox = boxes.get(relation.from);
          const toBox = boxes.get(relation.to);
          if (!fromBox || !toBox) return null;
          const a = center(fromBox);
          const b = center(toBox);
          const [fromLabel, toLabel] = CARDINALITY[relation.type] || ['', ''];
          const midX = (a.x + b.x) / 2;
          const midY = (a.y + b.y) / 2;
          const t = 0.22;
          return (
            <g key={relation.id}>
              <line
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={colors.interactive.primary}
                strokeWidth={1.5}
              />
              <text
                x={a.x + (b.x - a.x) * t}
                y={a.y + (b.y - a.y) * t - 5}
                fontSize={11}
                fill={colors.text.secondary}
              >
                {fromLabel}
              </text>
              <text
                x={b.x - (b.x - a.x) * t}
                y={b.y - (b.y - a.y) * t - 5}
                fontSize={11}
                fill={colors.text.secondary}
              >
                {toLabel}
              </text>
              {relation.label && (
                <text
                  x={midX}
                  y={midY - 6}
                  fontSize={11}
                  textAnchor="middle"
                  fill={colors.text.secondary}
                >
                  {relation.label}
                </text>
              )}
            </g>
          );
        })}

        {/* Entities */}
        {Array.from(boxes.values()).map(({ x, y: boxY, height, entity }) => (
          <g key={entity.id}>
            <rect
              x={x}
              y={boxY}
              width={BOX_WIDTH}
              height={height}
              rx={6}
              fill={colors.paper.background}
              stroke={colors.border.primary}
            />
            <rect
              x={x}
              y={boxY}
              width={BOX_WIDTH}
              height={HEADER_HEIGHT}
              rx={6}
              fill={colors.interactive.primary}
            />
            <text
              x={x + BOX_WIDTH / 2}
              y={boxY + HEADER_HEIGHT / 2 + 4}
              textAnchor="middle"
              fontSize={13}
              fontWeight="bold"
              fill={colors.primary.contrastText}
            >
              {entity.name || 'Untitled'}
            </text>
            {entity.fields.map((field, index) => (
              <text
                key={`${entity.id}-${field.name || index}`}
                x={x + 10}
                y={boxY + HEADER_HEIGHT + 14 + index * FIELD_HEIGHT}
                fontSize={11}
                fill={colors.text.primary}
              >
                {field.name || '(unnamed)'}
                <tspan fill={colors.text.secondary}> : {field.type}</tspan>
              </text>
            ))}
          </g>
        ))}
      </svg>
    </Box>
  );
};

ErdDiagram.propTypes = {
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      fields: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ),
  relations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      from: PropTypes.string,
      to: PropTypes.string,
      type: PropTypes.string,
      label: PropTypes.string,
    })
  ),
};

ErdDiagram.defaultProps = {
  entities: [],
  relations: [],
};

export default ErdDiagram;
